import React from 'react';
import { View, TouchableOpacity, Platform } from 'react-native';
import type { BlurViewProps } from 'expo-blur';
import { Text } from '@/components/gluestack/text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Cancel01Icon } from '@hugeicons-pro/core-solid-rounded';
import { Download01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { Photo } from '@/models/Photo';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import LikesCommentsOverlay from '@/components/domain/photos/LikesCommentsOverlay';
import { PhotoContextMenu } from '@/components/domain/photos/PhotoContextMenu';
import { useTranslation } from 'react-i18next';
import { useImageDownload } from '@/components/ui/image/useImageDownload';
import { useToastMessages } from '@/hooks/useToastMessages';
import { SafeBlurView } from '@/components/ui/SafeBlurView';

interface IImageViewerHeaderProps {
  photo?: Photo | null;
  onClose: () => void;
  onNavigateToProfile: () => void;
  isClosing: boolean;
  uiVisible: boolean;
  onBottomSheetOpenChange?: (open: boolean) => void;
  downloadSourceUri?: string | null;
  allowImageDownload?: boolean;
  /** Hides the likes/comments overlay and context menu, but keeps the owner info chip (non-Photo images). */
  disableSocialActions?: boolean;
  blurTarget?: BlurViewProps['blurTarget'];
}

export const ImageViewerHeader: React.FC<IImageViewerHeaderProps> = ({
  photo,
  onClose,
  onNavigateToProfile,
  isClosing,
  uiVisible,
  onBottomSheetOpenChange,
  downloadSourceUri,
  allowImageDownload,
  disableSocialActions = false,
  blurTarget,
}) => {
  const insets = useSafeAreaInsets();
  const isLargeScreen = useIsLargeScreen();
  const { t } = useTranslation();
  const { downloadImage, isDownloading } = useImageDownload();
  const { showSuccessToast, showErrorToast } = useToastMessages();

  const showNativeDownload =
    Platform.OS !== 'web' && !!downloadSourceUri && allowImageDownload === true;

  const handleNativeDownload = async () => {
    if (!downloadSourceUri) return;
    try {
      await downloadImage({
        uri: downloadSourceUri,
      });
      showSuccessToast(t('messages.download.success'), undefined, 'bottom');
    } catch (error) {
      if (error instanceof Error && error.name === 'MediaLibraryPermissionDenied') {
        showErrorToast(t('messages.download.permissionDenied'), undefined, 'bottom');
      } else {
        showErrorToast(t('messages.download.error'), error, 'bottom');
      }
    }
  };

  return (
    <View
      className="absolute left-0 right-0 top-0 z-20"
      pointerEvents={isClosing || !uiVisible ? 'none' : 'auto'}
      style={[
        {
          opacity: isClosing || !uiVisible ? 0 : 1,
        },
      ]}>
      <SafeBlurView
        blurTarget={blurTarget}
        intensity={80}
        tint="dark"
        className="flex-row items-center justify-between gap-4 px-4"
        style={{
          paddingTop: insets.top ? insets.top : 16,
          paddingBottom: 16,
        }}
        pointerEvents="auto">
        <View className="flex-1">
          {photo?.Member?.UserName && (
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={onNavigateToProfile}
              className="flex-row items-center gap-3"
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <ProfilePicture
                member={photo.Member}
                size="sm"
                className="h-10 w-10"
                showPresence={true}
                indicatorClassName='border-none'
                disableNavigation={true}
                showTooltip
              />
              <View className="flex-col">
                <Text className="font-semibold text-white">{photo.Member.UserName}</Text>
                {photo.DatePosted && (
                  <View className="flex-row">
                    <Text size="sm" className="text-white/80">{t('photos.posted')} </Text>
                    <RelativeTime
                      date={photo.DatePosted}
                      className="text-sm text-white/80"
                      textOnly={true}
                    />
                  </View>
                )}
              </View>
            </TouchableOpacity>
          )}
        </View>

        {isLargeScreen && photo && !disableSocialActions && (
          <View pointerEvents="box-none" className="absolute left-0 right-0 h-full items-center justify-center">
            <LikesCommentsOverlay
              photo={photo}
              iconSize={24}
              commentIconSize={22}
              textSize="md"
              iconClassName="text-white"
              disabled={photo.IsLocked}
              onBottomSheetOpenChange={onBottomSheetOpenChange}
            />
          </View>
        )}

        <View className="flex-row items-center gap-2">
          {showNativeDownload ? (
            <ButtonWrapper
              icon={Download01Icon}
              size="md"
              variant="solid"
              className="h-10 rounded-full bg-white/20"
              accessibilityLabel={t('messages.download.label')}
              onPress={handleNativeDownload}
              isDisabled={isDownloading}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            />
          ) : null}
          {photo && !photo.IsLocked && !disableSocialActions && (
            <PhotoContextMenu
              photo={photo}
              onCloseViewer={onClose}
              onBottomSheetOpenChange={onBottomSheetOpenChange}
            />
          )}
          <ButtonWrapper
            icon={Cancel01Icon}
            size="md"
            variant="solid"
            className="h-10 rounded-full bg-white/20"
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          />
        </View>
      </SafeBlurView>

    </View>
  );
};
